/**
 * The routes a phone gets (ROADMAP "The phone branch", D5 — verified against
 * App.tsx 2026-09-28).
 *
 * ONE list, read by both the phone tabs and the desktop hand-over gate, so the
 * two cannot drift the way the two hand-written menus did ([[BUGS #181]]).
 * Everything here is READING — research, the blog, the reference pages, and a
 * risk report someone shared. Creating an analysis (the rest of `/portfolio/*`),
 * and account pages hand over to the desktop on a phone. The assistant joined
 * the phone routes on 2026-09-29 (owner pick 1b).
 *
 * Patterns use react-router's spelling — literal segments, `:param` for one
 * segment, and a trailing `/*` for the route and everything below it — matched
 * by `matchesPattern` below. Not the router's own matcher: the repo-wide router
 * mock has none, and a pure function over strings needs no router to be tested.
 */

/** Does `pathname` match a pattern of literal / `:param` / trailing `/*` segments? */
export function matchesPattern(pattern: string, pathname: string): boolean {
  const split = (p: string) => p.split('/').filter(Boolean);
  const want = split(pattern);
  const have = split(pathname);
  const splat = want[want.length - 1] === '*';
  const fixed = splat ? want.slice(0, -1) : want;
  if (splat ? have.length < fixed.length : have.length !== fixed.length) return false;
  return fixed.every((seg, i) => seg.startsWith(':') || seg === have[i]);
}

export const PHONE_ROUTES: readonly string[] = [
  '/',
  '/research/*',
  '/stock-research/*',
  '/glossary/*',
  // A crisis replayed on sample portfolios — reading, like the glossary (SEO S2.2).
  '/stress-tests/*',
  '/sitemap',
  '/blog/*',
  '/academy',
  '/faq',
  '/about',
  '/security',
  '/privacy',
  '/terms',
  '/plans',
  // The assistant has its own full-screen phone page (owner pick 1b, 2026-09-29).
  '/chat',
  // A desktop reader shares, a phone reader opens — reading a report is in
  // scope even though creating one is not.
  '/portfolio/risk-results/share/:reportId',
  // A saved report, opened by id — READ on a phone (owner picks 6a/7a,
  // 2026-09-29). A stored read, never a billed run. Without an id the page is
  // the create flow, which still hands over (`/portfolio/*` below).
  '/portfolio/risk-results/:reportId',
  '/portfolio/risk-results/:reportId/:section',
  '/portfolio/rebalance/:reportId',
];

/**
 * The routes that hand over to the desktop on a phone — creating an analysis,
 * the assistant, and the account pages.
 *
 * Listed explicitly rather than taken as "everything not in PHONE_ROUTES", so
 * the 404 page still renders on a phone and a route nobody classified is not
 * told it "works best on a desktop". `phoneRouteContract.test.ts` fails when a
 * route in App.tsx is in neither list, so a new route forces the decision.
 */
export const DESKTOP_ROUTES: readonly string[] = [
  '/portfolio/*',
  '/watchlist',
  '/profile',
  '/billing',
];

/**
 * Phone routes that take the whole screen — no top bar, tabs or footer — and
 * draw their own header (owner pick 1b: the assistant, like a messaging app).
 */
export const PHONE_FULLSCREEN_ROUTES: readonly string[] = [
  '/chat',
  // A saved report reads full screen, with its own back-arrow header.
  '/portfolio/risk-results/:reportId',
  '/portfolio/risk-results/:reportId/:section',
  '/portfolio/rebalance/:reportId',
];

export function isPhoneFullscreenRoute(pathname: string): boolean {
  return PHONE_FULLSCREEN_ROUTES.some((pattern) => matchesPattern(pattern, pathname));
}

/** True when `pathname` is one a phone renders as the page. */
export function isPhoneRoute(pathname: string): boolean {
  return PHONE_ROUTES.some((pattern) => matchesPattern(pattern, pathname));
}

/** True when a phone shows the hand-over card instead of the page. A phone
 *  route wins, so a shared report under `/portfolio/*` is still read. */
export function handsOverToDesktop(pathname: string): boolean {
  return !isPhoneRoute(pathname) && DESKTOP_ROUTES.some((pattern) => matchesPattern(pattern, pathname));
}

/**
 * What the hand-over card calls the feature a phone reader just asked for —
 * *"Risk analysis works best on a desktop"* rather than a generic line
 * (owner, 2026-09-28). First match wins, so the specific `/portfolio/…` routes
 * sit above the catch-all.
 */
const HANDOVER_FEATURES: ReadonlyArray<{ pattern: string; name: string }> = [
  { pattern: '/portfolio/rebalance/*', name: 'Rebalancing' },
  { pattern: '/portfolio/signals', name: 'Signals' },
  { pattern: '/portfolio/*', name: 'Risk analysis' },
  { pattern: '/watchlist', name: 'Your watchlist' },
  { pattern: '/profile', name: 'Your profile' },
  { pattern: '/billing', name: 'Billing' },
];

/** The feature name for a hand-over route, or null for anything else. */
export function handoverFeatureName(pathname: string): string | null {
  const hit = HANDOVER_FEATURES.find((f) => matchesPattern(f.pattern, pathname));
  return hit ? hit.name : null;
}

/** The bottom tabs: reading only (owner, 2026-09-18), with Home on the far
 *  left as the phone's front door (owner, 2026-09-28). */
export const PHONE_TABS = [
  { label: 'Home', path: '/', matches: ['/'] },
  { label: 'Research', path: '/research', matches: ['/research/*', '/stock-research/*'] },
  { label: 'Blog', path: '/blog', matches: ['/blog/*'] },
] as const;

/** The tab the current route belongs to, or null (home, legal pages…). */
export function activePhoneTab(pathname: string): string | null {
  const tab = PHONE_TABS.find((t) =>
    t.matches.some((pattern) => matchesPattern(pattern, pathname)),
  );
  return tab ? tab.path : null;
}
